import Link from "@tiptap/extension-link"
import { EditorContent, useEditor } from "@tiptap/react"
import StarterKit from "@tiptap/starter-kit"
import {
  Bold,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Redo,
  Undo,
  Unlink,
} from "lucide-react"
import { useCallback, useState } from "react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { cn } from "@/lib/utils"

interface TiptapEditorProps {
  content: string
  onChange: (html: string) => void
  placeholder?: string
  className?: string
}

interface ToolbarButtonProps {
  onClick: () => void
  isActive?: boolean
  disabled?: boolean
  children: React.ReactNode
  title: string
}

function ToolbarButton({
  onClick,
  isActive,
  disabled,
  children,
  title,
}: ToolbarButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={cn(
        "p-2 rounded-md transition-colors",
        "hover:bg-slate-100 dark:hover:bg-slate-800",
        "disabled:opacity-40 disabled:cursor-not-allowed",
        isActive && "bg-slate-100 dark:bg-slate-800 text-emerald-600"
      )}
    >
      {children}
    </button>
  )
}

export function TiptapEditor({
  content,
  onChange,
  placeholder = "Add your editorial notes...",
  className,
}: TiptapEditorProps) {
  const [linkUrl, setLinkUrl] = useState("")
  const [linkOpen, setLinkOpen] = useState(false)

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        code: false,
        blockquote: false,
        horizontalRule: false,
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class:
            "text-emerald-600 underline underline-offset-2 hover:text-emerald-700",
        },
      }),
    ],
    content,
    editorProps: {
      attributes: {
        class: cn(
          "prose prose-slate dark:prose-invert prose-sm max-w-none",
          "min-h-[120px] px-4 py-3 outline-none",
          "focus:ring-0",
          "[&_p]:my-2 [&_ul]:my-2 [&_ol]:my-2",
          "[&_li]:my-0.5"
        ),
      },
    },
    onUpdate: ({ editor: e }) => {
      onChange(e.getHTML())
    },
  })

  const setLink = useCallback(() => {
    if (!editor || !linkUrl) return

    if (linkUrl === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run()
    } else {
      editor
        .chain()
        .focus()
        .extendMarkRange("link")
        .setLink({ href: linkUrl })
        .run()
    }

    setLinkUrl("")
    setLinkOpen(false)
  }, [editor, linkUrl])

  const removeLink = useCallback(() => {
    if (!editor) return
    editor.chain().focus().unsetLink().run()
  }, [editor])

  if (!editor) {
    return (
      <div
        className={cn(
          "rounded-lg border bg-muted/30 animate-pulse",
          className
        )}
      >
        <div className="h-10 border-b" />
        <div className="min-h-[120px]" />
      </div>
    )
  }

  return (
    <div
      className={cn(
        "rounded-lg border border-slate-200 dark:border-slate-800",
        "bg-white dark:bg-slate-950",
        "focus-within:ring-2 focus-within:ring-emerald-500/20 focus-within:border-emerald-500/50",
        "transition-all duration-200",
        className
      )}
    >
      {/* Toolbar */}
      <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-slate-100 dark:border-slate-800">
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive("bold")}
          title="Bold (⌘B)"
        >
          <Bold className="h-4 w-4" />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive("italic")}
          title="Italic (⌘I)"
        >
          <Italic className="h-4 w-4" />
        </ToolbarButton>

        <div className="w-px h-5 bg-slate-200 dark:bg-slate-700 mx-1" />

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          isActive={editor.isActive("bulletList")}
          title="Bullet List"
        >
          <List className="h-4 w-4" />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          isActive={editor.isActive("orderedList")}
          title="Numbered List"
        >
          <ListOrdered className="h-4 w-4" />
        </ToolbarButton>

        <div className="w-px h-5 bg-slate-200 dark:bg-slate-700 mx-1" />

        <Popover open={linkOpen} onOpenChange={setLinkOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              title="Add Link"
              className={cn(
                "p-2 rounded-md transition-colors",
                "hover:bg-slate-100 dark:hover:bg-slate-800",
                editor.isActive("link") &&
                  "bg-slate-100 dark:bg-slate-800 text-emerald-600"
              )}
            >
              <LinkIcon className="h-4 w-4" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-3" align="start">
            <div className="flex gap-2">
              <Input
                placeholder="https://..."
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault()
                    setLink()
                  }
                }}
                className="flex-1"
              />
              <Button size="sm" onClick={setLink}>
                Add
              </Button>
            </div>
          </PopoverContent>
        </Popover>

        {editor.isActive("link") && (
          <ToolbarButton onClick={removeLink} title="Remove Link">
            <Unlink className="h-4 w-4" />
          </ToolbarButton>
        )}

        <div className="flex-1" />

        <ToolbarButton
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          title="Undo (⌘Z)"
        >
          <Undo className="h-4 w-4" />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          title="Redo (⌘⇧Z)"
        >
          <Redo className="h-4 w-4" />
        </ToolbarButton>
      </div>

      {/* Editor Content */}
      <EditorContent editor={editor} />

      {/* Placeholder when empty */}
      {editor.isEmpty && (
        <div className="pointer-events-none absolute top-[52px] left-4 text-slate-400 dark:text-slate-500 text-sm">
          {placeholder}
        </div>
      )}
    </div>
  )
}
